import type { PublicDirectory, PublicService } from "../types";
import { formatOpenDay, formatOpenTime, formatTime } from "./directory";
import { formatServicePrice } from "@/features/booking/utils/client-format";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/** A price as the site prints it: the amount, and whether it reads "from". */
type PriceTag = { amount: number; from: boolean };

export const minutesLabel = (minutes: number) => m.services_minutes({ count: minutes });

/**
 * "$60", "From $60" or "Free". Amounts are in the currency's minor units, the way the booking
 * pack stores them, so the pack's own formatter does the reading: one rule for money everywhere.
 */
export const priceLabel = (price: PriceTag | null, currency: string) =>
  price ? formatServicePrice({ amount: price.amount, from: price.from, currency }) : null;

export const servicePriceLabel = (service: PublicService, currency: string) =>
  priceLabel(service.priceAmount === null ? null : { amount: service.priceAmount, from: service.priceFrom }, currency);

/** "10:20" on the given clock, in the app's language rather than the browser's. */
export const timeLabel = (startAt: string, timeZone: string) => formatTime(startAt, timeZone, getLocale());

/** "Today 10:20" or "Tue 09:00" on the given clock. */
export const openTimeLabelIn = (startAt: string, timeZone: string) =>
  formatOpenTime(startAt, { timeZone, todayLabel: m.landing_calendar_today(), locale: getLocale() });

/** "Today 10:20" or "Tue 09:00", in the studio's time zone. */
export const openTimeLabel = (startAt: string, directory: PublicDirectory) =>
  openTimeLabelIn(startAt, directory.business.timeZone);

/** The element id a coach's profile carries on the coaches page. */
export const coachAnchor = (specialistId: string) => `coach-${specialistId}`;

/** "Today" or "Thursday" on the given clock. */
export const openDayLabelIn = (startAt: string, timeZone: string) =>
  formatOpenDay(startAt, { timeZone, todayLabel: m.landing_calendar_today(), locale: getLocale() });

/**
 * What a page says when there is nothing to book: call, write, or come back later, depending on
 * which way to reach the trainer the page has.
 */
export const reachOutLabel = (contact: { kind: "phone" | "email" } | null) =>
  contact?.kind === "phone"
    ? m.landing_calendar_empty_body()
    : contact?.kind === "email"
      ? m.landing_calendar_empty_write()
      : m.landing_calendar_empty_later();
